import { useTranslate } from "@opensea/next-translate"
import { SmartphoneFilled, VerifiedUser, VpnKey } from "@opensea/ui-kit/icons"
import type { MfaMethod } from "@privy-io/react-auth"
import { useMfa, useMfaEnrollment, usePrivy } from "@privy-io/react-auth"
import React from "react"
import { Text } from "@/design-system/Text"
import { ManageSmsModal } from "@/features/wallet/components/ManageSmsModal"
import { MfaMethodItem } from "@/features/wallet/components/MfaMethodsModal/components/MfaMethodItem"
import { SUPPORTED_MFA_METHODS } from "@/features/wallet/components/MfaMethodsModal/constants"
import { useGlobalModal } from "@/providers/GlobalModal"
import { ManageTotpModal } from "../../ManageTotpModal"

export function MfaMethodsModalBody() {
  const t = useTranslate("mfa")
  const { unenrollWithSms, unenrollWithTotp } = useMfaEnrollment()
  const { openModal, closeModal } = useGlobalModal()
  const { user } = usePrivy()
  const { promptMfa } = useMfa()

  if (!user) {
    return null
  }
  const { mfaMethods: enrolledMfaMethods } = user

  const unenrolledMfaMethods = SUPPORTED_MFA_METHODS.filter(
    mfaMethod => !enrolledMfaMethods.includes(mfaMethod),
  )

  const canUnenroll = enrolledMfaMethods.length > 1

  const icons = {
    totp: <VerifiedUser />,
    sms: <SmartphoneFilled />,
    passkey: <VpnKey />,
  } as const

  const descriptions = {
    totp: t("enrollmentStatus.modal.totpDescription", "Most secure option"),
    sms: t("enrollmentStatus.modal.smsDescription", "US & Canada only"),
    passkey: t("enrollmentStatus.modal.passkeyDescription", "Use a passkey"),
  } as const

  const displayNames = {
    totp: t("enrollmentStatus.modal.totp", "Authenticator"),
    sms: t("enrollmentStatus.modal.sms", "SMS"),
    passkey: t("enrollmentStatus.modal.passkey", "Passkey"),
  } as const

  const handleManageMfa = async (
    manageAction: "setup" | "edit",
    mfaMethod: MfaMethod,
  ) => {
    try {
      await promptMfa()

      switch (mfaMethod) {
        case "sms":
          openModal(
            <ManageSmsModal
              isEdit={manageAction === "edit"}
              onClose={closeModal}
            />,
          )
          break
        case "totp":
          openModal(<ManageTotpModal onClose={closeModal} />)
          break
      }
    } catch (e) {
      // Do nothing
    }
  }

  const handleUnenroll = async (mfaMethod: MfaMethod) => {
    try {
      switch (mfaMethod) {
        case "sms": {
          await unenrollWithSms()
          return
        }
        case "totp": {
          await unenrollWithTotp()
        }
      }
    } catch (e) {
      // Do nothing
    }
  }

  return (
    <div>
      {enrolledMfaMethods.length > 0 && (
        <div>
          <Text weight="semibold">
            {t("enrollmentStatus.modal.otherMethods", "Enrolled")}
          </Text>
          <ul className="mt-4 flex flex-col gap-4">
            {enrolledMfaMethods.map(mfaMethod => (
              <li key={mfaMethod}>
                <MfaMethodItem
                  canUnenroll={canUnenroll}
                  description={descriptions[mfaMethod]}
                  enrolled
                  icon={icons[mfaMethod]}
                  label={displayNames[mfaMethod]}
                  onEdit={async () => {
                    await handleManageMfa("edit", mfaMethod)
                  }}
                  onUnenroll={async () => {
                    await handleUnenroll(mfaMethod)
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      {unenrolledMfaMethods.length > 0 && (
        <div className="mt-8">
          <Text weight="semibold">
            {t(
              "enrollmentStatus.modal.enrolledMethods",
              {
                one: "Other method",
                other: "Other methods",
              },
              { count: unenrolledMfaMethods.length },
            )}
          </Text>
          <ul className="mt-4">
            {unenrolledMfaMethods.map(mfaMethod => (
              <li key={mfaMethod}>
                <MfaMethodItem
                  canUnenroll={false}
                  description={descriptions[mfaMethod]}
                  enrolled={false}
                  icon={icons[mfaMethod]}
                  label={displayNames[mfaMethod]}
                  onSetUp={async () => {
                    await handleManageMfa("setup", mfaMethod)
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
